import React, { useState } from "react";
import { useNavigate, useLocation, useSearchParams, Navigate, Link } from "react-router-dom";
import { LogIn, Loader2, MessageCircle, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { login, isAuthed } from "../services/teacherApi";
import LogoMark from "../components/LogoMark";
import wordmarkUrl from "../components/adarshabani-wordmark.svg";

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation() as any;
  const [params] = useSearchParams();

  // Two ways to arrive holding a destination: RequireAuth pushes router state,
  // while the 401 interceptor in teacherApi does a hard navigation (the tree is
  // mid-render against a dead session) and can only carry a query string.
  //
  // Only ever an in-app path — `//evil.com` is a protocol-relative URL the
  // browser would treat as another origin, so a leading `//` is out.
  const nextRaw = params.get("next") || "";
  const next = nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : "";
  const from = next || loc.state?.from?.pathname || "/";

  // The session ended rather than the user choosing to sign out. Saying so is
  // the whole point of the redirect — what used to happen was the dead screen
  // printing the server's word "unauthenticated" and nothing else.
  const expired = params.get("expired") === "1";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in — redirect declaratively instead of calling nav() during
  // render (which React warns about and can double-fire).
  if (isAuthed()) return <Navigate to={from} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(username.trim().toLowerCase(), password);
      nav(from, { replace: true });
    } catch (e: any) {
      if (e?.body?.error === "pending_approval") setErr("Your account is awaiting admin approval.");
      else setErr(e?.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-void text-slate-200 grid place-items-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm glass-panel rounded-2xl p-7"
      >
        <div className="flex items-center gap-3 mb-6">
          <LogoMark className="w-12 h-12 text-white shrink-0" />
          <div>
            <div className="font-display text-2xl tracking-tight uppercase text-white leading-none">KINETIX</div>
            <img src={wordmarkUrl} alt="by Adarshabani" className="h-3 mt-1.5 opacity-80" />
            <div className="font-mono text-[10px] text-slate-400 uppercase tracking-widest mt-1.5">Teacher</div>
          </div>
        </div>

        <h1 className="text-xl font-semibold text-white mb-1">Sign in</h1>
        <p className="text-sm text-slate-400 mb-6">Sign in with your username.</p>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Username</span>
            <input
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              placeholder="Username"
              className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-cobalt-500/60"
              required
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-cobalt-500/60"
              required
            />
          </label>

          {expired && !err && (
            <div className="flex items-start gap-2 text-xs text-ochre-300 bg-ochre-500/10 border border-ochre-500/30 rounded-lg px-3 py-2">
              <Clock size={14} className="shrink-0 mt-0.5" />
              <span>Your session ended — please sign in again.</span>
            </div>
          )}

          {err && (
            <div className="text-xs text-madder-400 bg-madder-500/10 border border-madder-500/30 rounded-lg px-3 py-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !username || !password}
            className="mt-2 flex items-center justify-center gap-2 py-2.5 bg-cobalt-600 hover:bg-cobalt-500 disabled:opacity-50 disabled:cursor-not-allowed text-oncolor font-mono text-sm tracking-widest rounded-lg transition-all shadow-lg shadow-cobalt-500/20"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
            {busy ? "SIGNING_IN" : "SIGN_IN"}
          </button>

          {/* Forgot your password? Don't reset it — get a one-time code instead.
              This is the path most staff should be taking. */}
          <Link
            to="/otp-login"
            className="flex items-center justify-center gap-2 py-2.5 border border-white/15 hover:border-white/30 hover:bg-white/5 text-slate-200 font-mono text-sm tracking-widest rounded-lg transition-all"
          >
            <MessageCircle size={16} />
            SIGN_IN_WITH_CODE
          </Link>

          <div className="flex items-center justify-between text-xs pt-1">
            <Link to="/register" className="text-cobalt-400 hover:underline">New here? Register</Link>
            <Link to="/forgot-password" className="text-slate-400 hover:underline">Forgot password?</Link>
          </div>
        </form>
      </motion.div>
    </main>
  );
}
